import { useCallback, useState } from "react";

import QUERY_TAGS from "../search/queryTags";
import { invalidateTags } from "../search/queryCache";
import { getApiErrorStatus } from "../../lib/api/apiError";
import { mapTopUpInitialization, mapWallet } from "../checkout/paymentsMappers";
import { clearPendingTopUp, savePendingTopUp } from "../checkout/topUpReturn";
import { confirmTopUp } from "../checkout/topUpConfirm";
import { launchPaystack, launchPaystackPopup } from "../checkout/paystackLauncher";
import { fetchWalletBalance, initiateWalletTopUp } from "./financeService";
import { TOP_UP_CURRENCY } from "./financeMoney";
import { FINANCE_PATH, financeReturnUrl } from "./financeRoutes";

const invalidateWallet = () => {
  invalidateTags([QUERY_TAGS.PAYMENT_WALLET, QUERY_TAGS.PAYMENT_TRANSACTIONS]);
};

const useFinanceTopUp = () => {
  const [phase, setPhase] = useState("idle");
  const [blocked, setBlocked] = useState(null);

  const confirm = useCallback(async (pending) => {
    setPhase("confirming");

    try {
      const outcome = await confirmTopUp(pending);

      invalidateWallet();

      return {
        status: outcome.confirmed ? "credited" : "pending",
        wallet: outcome.wallet,
        amount: { value: pending.amount, currency: pending.currencyCode },
      };
    } finally {
      setPhase("idle");
    }
  }, []);

  const cancel = useCallback(() => {
    clearPendingTopUp();
    invalidateWallet();
    setPhase("idle");

    return { status: "cancelled" };
  }, []);

  const settle = useCallback(
    async (outcome, initiation, pending) => {
      if (outcome === "redirected") return { status: "redirected" };

      if (outcome === "popup-blocked") {
        setBlocked({ initiation, pending });
        setPhase("idle");

        return { status: "popup-blocked" };
      }

      if (outcome === "success" || outcome === "closed") return confirm(pending);

      return cancel();
    },
    [confirm, cancel]
  );

  const start = useCallback(
    async ({ amount, currencyCode = TOP_UP_CURRENCY, email }) => {
      if (!(amount > 0)) return { status: "invalid" };
      if (!email) return { status: "email-required" };

      setPhase("initiating");

      let initiation = null;
      let balanceBefore = null;

      try {
        const wallet = mapWallet(await fetchWalletBalance(currencyCode));

        if (!wallet) {
          setPhase("idle");

          return { status: "wallet-unavailable" };
        }

        balanceBefore = wallet.balance.value;
        initiation = mapTopUpInitialization(
          await initiateWalletTopUp({
            walletId: wallet.id,
            amount,
            returnUrl: financeReturnUrl(),
          })
        );

        if (!initiation) {
          setPhase("idle");

          return { status: "initiate-failed" };
        }
      } catch (error) {
        setPhase("idle");

        return {
          status: getApiErrorStatus(error) === 404 ? "wallet-unavailable" : "initiate-failed",
          error,
        };
      }

      const pending = {
        kind: "topup",
        reference: initiation.reference,
        amount,
        currencyCode,
        balanceBefore,
        returnTo: FINANCE_PATH,
      };

      savePendingTopUp(pending);
      setPhase("awaiting");

      return settle(await launchPaystack(initiation, { mode: "redirect" }), initiation, pending);
    },
    [settle]
  );

  const resumeBlocked = useCallback(async () => {
    if (!blocked) return { status: "invalid" };

    const { initiation, pending } = blocked;

    setBlocked(null);
    setPhase("awaiting");

    return settle(await launchPaystackPopup(initiation), initiation, pending);
  }, [blocked, settle]);

  const dismissBlocked = useCallback(() => {
    setBlocked(null);

    return cancel();
  }, [cancel]);

  return {
    phase,
    busy: phase === "initiating" || phase === "confirming",
    blocked: Boolean(blocked),
    start,
    confirm,
    cancel,
    resumeBlocked,
    dismissBlocked,
  };
};

export default useFinanceTopUp;
